import { CommunityAnnouncements } from '@platform/client';
import { Input, Modal, InputDropdown } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AnnouncementTypeEnum, CommunityAnnouncement } from '@platform/client/schemas';

interface UpdateCommunityAnnouncementModalProps {
  isOpen: boolean;
  onClose: () => void;
  communityAnnouncement: CommunityAnnouncement;
  page?: number;
  limit?: number;
}

export const UpdateCommunityAnnouncementModal = (props: UpdateCommunityAnnouncementModalProps) => {
  const { communityAnnouncement, isOpen, onClose, page, limit } = props;

  const [title, setTitle] = useState(communityAnnouncement.title);
  const [description, setDescription] = useState(communityAnnouncement.description);
  const [cta, setCta] = useState(communityAnnouncement.callToAction);
  const [link, setLink] = useState(communityAnnouncement.link || '');
  const [type, setType] = useState<AnnouncementTypeEnum>(communityAnnouncement.type);

  const queryClient = useQueryClient();

  const submitCommunityAnnouncement = CommunityAnnouncements.useUpdateCommunityAnnouncement({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CommunityAnnouncements.getGetCommunityAnnouncementsQueryKey({
            limit: limit || 0,
            page: page || 0,
          }),
        });
      },
    },
  });

  const updateCommunityAnnouncement = () => {
    submitCommunityAnnouncement.mutate({
      data: {
        id: communityAnnouncement.id,
        title,
        description,
        callToAction: cta,
        link,
        type,
      },
    });

    closeModal();
  };

  const closeModal = () => {
    onClose();
    setTitle('');
    setDescription('');
    setCta('');
    setLink('');
  };

  return (
    <Modal
      variant='dialog'
      title='Update Community Announcement'
      isOpen={isOpen}
      headerDivision='divider'
      ignoreExtraPadding={true}
      onClose={closeModal}
      primaryCTA={{
        text: 'Update Community Announcement',
        callback: updateCommunityAnnouncement,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: closeModal,
      }}
    >
      <div className='md:w-[460px]'>
        <div className='flex flex-col gap-4 w-full'>
          <Input
            label='Title'
            type='inline'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <Input
            label='Call to Action'
            type='inline'
            value={cta}
            onChange={(e) => setCta(e.target.value)}
          />
          <InputDropdown
            label='Type'
            type='default'
            inputDisabled
            defaultSelectedKey={type}
            onSelectionChange={(value) => setType(value as AnnouncementTypeEnum)}
            items={Object.values(AnnouncementTypeEnum).map((type) => ({
              text: type,
              textValue: type,
            }))}
          />
          <Input
            label='Link'
            type='inline'
            value={link}
            onChange={(e) => setLink(e.target.value)}
          />
          <Input
            label='Description'
            type='multiline'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
};
